import { Accordion, Avatar, Stack } from '@mantine/core'
import { GppMaybe, MyLocation, ToggleOff, ToggleOn, VerifiedUser } from '@mui/icons-material'
import { TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	blockUserTemp,
	getOneUser,
	setVerifyRegularUser,
	setVerifyUser,
	unBlockUserTemp,
} from 'src/apis/userAPI'
import CustomGoBack from 'src/components/CustomComponents/CustomGoBack'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import ModalForm from 'src/components/Modal/ModalForm'
import TableComponent from 'src/components/TableComponent'
import ListDevice from 'src/components/ListDevice/ListDevice'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { DatingInfo, FieldType, UserData } from 'src/interfaces'
import { formatDateTime, userDataFields } from 'src/variable/constant'
import { useNavigate } from 'react-router-dom'
import ModalUpdateUserAddress from '../Users/ModalUpdateUserAddress'
import styles from './UserDetail.module.scss'

const blockTypeOpts = [
	{ label: 'One day', value: 'ONE_DAY' },
	{ label: 'Three days', value: 'THREE_DAYS' },
	{ label: 'Permanent', value: 'FOREVER' },
]

const timestampFields = new Set([
	'name_updated_at',
	'time_open_hangout',
	'last_time_update_address',
	'version_updated_at',
	'last_login_at',
	'online_time',
	'wallet_updated_at',
	'blocked_at',
	'unblocked_at',
])

const dateTimeFields = new Set(['created_at', 'updated_at', 'deleted_at'])
const dateFields = new Set(['birthday'])
const defaultFieldValues = new Set(userDataFields.map((field) => field.value))

const accountFields = [
	{ key: 'id', label: 'User ID', fullWidth: true },
	{ key: 'account_type', label: 'Account type' },
	{ key: 'phone', label: 'Phone' },
	{ key: 'prefix_phone', label: 'Phone prefix' },
	{ key: 'email', label: 'Email' },
	{ key: 'login_type', label: 'Login type' },
	{ key: 'language', label: 'Language' },
	{ key: 'language_for_translate', label: 'Translate language' },
	{ key: 'country_code', label: 'Country code' },
	{ key: 'country', label: 'Country' },
]

const statusFields = [
	{ key: 'status', label: 'Active status', format: 'boolean' },
	{ key: 'visibility', label: 'Visibility' },
	{ key: 'is_verified', label: 'UniVini verified', format: 'boolean' },
	{ key: 'is_regular', label: 'Regular user', format: 'boolean' },
	{ key: 'is_dev', label: 'Dev user', format: 'boolean' },
	{ key: 'is_accept_notification', label: 'Accept notification', format: 'boolean' },
	{ key: 'blocked_at', label: 'Blocked at', format: 'timestamp' },
	{ key: 'unblocked_at', label: 'Unblocked at', format: 'timestamp' },
	{ key: 'blocked_type', label: 'Blocked type' },
	{ key: 'blocked_reason', label: 'Blocked reason' },
	{ key: 'blocked_by', label: 'Blocked by' },
	{ key: 'blocked_by_user', label: 'Blocked by user' },
	{ key: 'amount_of_appeal', label: 'Amount of appeal' },
]

const profileFields = [
	{ key: 'name', label: 'Name' },
	{ key: 'name_updated_at', label: 'Name updated at', format: 'timestamp' },
	{ key: 'gender', label: 'Gender' },
	{ key: 'birthday', label: 'Birthday', format: 'date' },
	{ key: 'age', label: 'Age' },
	{ key: 'is_hide_age', label: 'Hide age', format: 'boolean' },
	{ key: 'about_me', label: 'About me', fullWidth: true },
	{ key: 'avatar', label: 'Avatar', format: 'image', fullWidth: true },
	{ key: 'cover', label: 'Cover', format: 'image', fullWidth: true },
	{ key: 'share_link', label: 'Share link', fullWidth: true },
	{ key: 'invite_code', label: 'Invite code' },
]

const activityFields = [
	{ key: 'platform', label: 'Platform' },
	{ key: 'version', label: 'Version' },
	{ key: 'version_updated_at', label: 'Version updated at', format: 'timestamp' },
	{ key: 'last_login_at', label: 'Last login at', format: 'timestamp' },
	{ key: 'online_time', label: 'Online time', format: 'timestamp' },
	{ key: 'wallet', label: 'Wallet' },
	{ key: 'wallet_updated_at', label: 'Wallet updated at', format: 'timestamp' },
	{ key: 'amount_of_friend', label: 'Friends' },
	{ key: 'created_at', label: 'Created at', format: 'datetime' },
	{ key: 'updated_at', label: 'Updated at', format: 'datetime' },
	{ key: 'deleted_at', label: 'Deleted at', format: 'datetime' },
]

const locationFields = [
	{ key: 'address', label: 'GPS', fullWidth: true },
	{ key: 'latitude', label: 'GPS latitude' },
	{ key: 'longitude', label: 'GPS longitude' },
	{ key: 'position', label: 'GPS position', format: 'json', fullWidth: true },
	{ key: 'address_local', label: 'Local address', fullWidth: true },
	{ key: 'latitude_local', label: 'Local latitude' },
	{ key: 'longitude_local', label: 'Local longitude' },
	{ key: 'position_local', label: 'Local position', format: 'json', fullWidth: true },
	{ key: 'area_name', label: 'Area name' },
	{ key: 'last_time_update_address', label: 'Last address update', format: 'timestamp' },
]

const mbProfileFields = [
	{ key: 'mode', label: 'Mode' },
	{ key: 'who_i_am', label: 'I am', fullWidth: true },
	{ key: 'looking_for', label: "I'm looking for", fullWidth: true },
	{ key: 'i_can_offer', label: 'I can offer', fullWidth: true },
	{ key: 'i_am_interested_in', label: 'Interested in', fullWidth: true },
	{ key: 'i_am_from', label: 'I am from' },
	{ key: 'category_list', label: 'Category list', format: 'array', fullWidth: true },
	{ key: 'network_favorites', label: 'Network favorites', format: 'array', fullWidth: true },
	{ key: 'country_visited', label: 'Countries visited', fullWidth: true },
	{ key: 'country_visited_array', label: 'Countries visited array', format: 'array', fullWidth: true },
	{ key: 'country_lived', label: 'Countries lived', fullWidth: true },
	{ key: 'country_lived_array', label: 'Countries lived array', format: 'array', fullWidth: true },
	{ key: 'languages_can_speak', label: 'Languages can speak', fullWidth: true },
	{ key: 'languages_can_speak_array', label: 'Languages can speak array', format: 'array', fullWidth: true },
	{ key: 'is_open_hangout', label: 'Open hangout', format: 'boolean' },
	{ key: 'time_open_hangout', label: 'Open hangout time', format: 'timestamp' },
	{ key: 'title_open_hangout', label: 'Open hangout title', fullWidth: true },
	{ key: 'dating_now_toggle', label: 'Dating now toggle', format: 'boolean' },
]

const systemFields = [
	{ key: 'complete_profile', label: 'Complete profile', format: 'json', fullWidth: true },
	{ key: 'mini_app_config', label: 'Mini app config', format: 'json', fullWidth: true },
	{ key: 'status_of_tutorial', label: 'Tutorial status', format: 'json', fullWidth: true },
	{ key: 'permissions', label: 'Permissions', format: 'json', fullWidth: true },
	{ key: 'lmb_onboarding_process', label: 'LMB onboarding' },
	{ key: 'fmg_onboarding_process', label: 'FMG onboarding' },
]

const datingFields = [
	{ key: 'id', label: 'Dating ID' },
	{ key: 'user_type', label: 'User type' },
	{ key: 'name', label: 'Display name' },
	{ key: 'gender', label: 'Gender' },
	{ key: 'birthday', label: 'Birthday', format: 'datetime' },
	{ key: 'age', label: 'Age' },
	{ key: 'address', label: 'Address', fullWidth: true },
	{ key: 'country', label: 'Country' },
	{ key: 'jobs', label: 'Job' },
	{ key: 'favorites', label: 'Favorites', fullWidth: true },
	{ key: 'i_am_interested_in', label: 'Interested in', fullWidth: true },
	{ key: 'country_visited', label: 'Countries visited', fullWidth: true },
	{ key: 'languages_can_speak', label: 'Languages can speak', fullWidth: true },
	{ key: 'amount_of_like', label: 'Amount of like' },
	{ key: 'is_block', label: 'Is block', format: 'boolean' },
	{ key: 'is_open_dating', label: 'Open dating', format: 'boolean' },
	{ key: 'thinking', label: 'Thinking', fullWidth: true },
	{ key: 'about_me', label: 'About me', fullWidth: true },
	{ key: 'created_at', label: 'Created at', format: 'datetime' },
	{ key: 'updated_at', label: 'Updated at', format: 'datetime' },
]

const isEmptyValue = (value) =>
	value === null ||
	value === undefined ||
	value === '' ||
	(Array.isArray(value) && value.length === 0)

const formatDateLikeValue = (value, format = formatDateTime) => {
	if (isEmptyValue(value)) return '-'

	const parsed = dayjs(value)
	return parsed.isValid() ? parsed.format(format) : String(value)
}

const formatTimestampValue = (value) => {
	if (isEmptyValue(value)) return '-'

	const timestamp = Number(value)
	if (!Number.isFinite(timestamp)) return String(value)

	const parsed = dayjs(timestamp)
	return parsed.isValid() ? parsed.format(formatDateTime) : String(value)
}

const formatPrimitiveValue = (value) => {
	if (isEmptyValue(value)) return '-'
	if (typeof value === 'boolean') return value ? 'Yes' : 'No'
	return String(value)
}

function StatusBadge({ active, trueLabel = 'Yes', falseLabel = 'No' }) {
	return (
		<span className={`${styles.badge} ${active ? styles.badgeSuccess : styles.badgeMuted}`}>
			{active ? trueLabel : falseLabel}
		</span>
	)
}

function TagList({ items }) {
	if (!items?.length) {
		return <div className={styles.fieldValue}>-</div>
	}

	return (
		<div className={styles.chipList}>
			{items.map((item, index) => (
				<div className={styles.chip} key={`${item}-${index}`}>
					{formatPrimitiveValue(item)}
				</div>
			))}
		</div>
	)
}

function JsonValue({ value }) {
	if (isEmptyValue(value)) {
		return <div className={styles.fieldValue}>-</div>
	}

	return (
		<pre className={styles.jsonBlock}>
			{JSON.stringify(value, null, 2)}
		</pre>
	)
}

function ImageValue({ value, label }) {
	if (isEmptyValue(value)) {
		return <div className={styles.fieldValue}>-</div>
	}

	return (
		<div className={styles.imageValue}>
			<img alt={label} src={value} />
			<a href={value} target="_blank" rel="noreferrer">
				{value}
			</a>
		</div>
	)
}

function FieldValue({ field, value }) {
	const format = field.format

	if (format === 'boolean') {
		if (isEmptyValue(value)) return <div className={styles.fieldValue}>-</div>
		return <StatusBadge active={!!value} />
	}

	if (format === 'timestamp' || timestampFields.has(field.key)) {
		return <div className={styles.fieldValue}>{formatTimestampValue(value)}</div>
	}

	if (format === 'datetime' || dateTimeFields.has(field.key)) {
		return <div className={styles.fieldValue}>{formatDateLikeValue(value)}</div>
	}

	if (format === 'date' || dateFields.has(field.key)) {
		return <div className={styles.fieldValue}>{formatDateLikeValue(value, 'DD/MM/YYYY')}</div>
	}

	if (format === 'array' || Array.isArray(value)) {
		return <TagList items={Array.isArray(value) ? value : []} />
	}

	if (format === 'json' || (typeof value === 'object' && value !== null)) {
		return <JsonValue value={value} />
	}

	if (format === 'image') {
		return <ImageValue value={value} label={field.label} />
	}

	return <div className={styles.fieldValue}>{formatPrimitiveValue(value)}</div>
}

function DetailField({ field, data }) {
	return (
		<div className={field.fullWidth ? styles.fieldItemFull : styles.fieldItem}>
			<div className={styles.fieldLabel}>{field.label}</div>
			<FieldValue field={field} value={data?.[field.key]} />
		</div>
	)
}

function DetailSection({ title, description = null, fields, data, isFieldVisible }) {
	const visibleFields = fields.filter((field) => isFieldVisible(field.key))

	if (!visibleFields.length) {
		return null
	}

	return (
		<section className={styles.section}>
			<div className={styles.sectionHeader}>
				<div>
					<h2 className={styles.sectionTitle}>{title}</h2>
					{description ? <p className={styles.sectionDescription}>{description}</p> : null}
				</div>
			</div>
			<div className={styles.fieldGrid}>
				{visibleFields.map((field) => (
					<DetailField key={field.key} field={field} data={data} />
				))}
			</div>
		</section>
	)
}

function DatingSection({ dating }: { dating: DatingInfo }) {
	if (!dating) {
		return null
	}

	const datingImages = [dating.avatar, ...(dating.images ?? [])].filter(Boolean)

	return (
		<section className={styles.section}>
			<Accordion className={styles.sectionAccordion} variant="separated">
				<Accordion.Item value="dating-details">
					<Accordion.Control className={styles.sectionControl}>
						<div>
							<h2 className={styles.sectionTitle}>Dating details</h2>
							<p className={styles.sectionDescription}>Profile data rendered from the dating table.</p>
						</div>
					</Accordion.Control>
					<Accordion.Panel>
						<div className={styles.fieldGrid}>
							{datingFields.map((field) => (
								<DetailField key={field.key} field={field} data={dating} />
							))}

							<div className={styles.fieldItem}>
								<div className={styles.fieldLabel}>Thinking time</div>
								<div className={styles.fieldValue}>{formatTimestampValue(dating.thinking_time)}</div>
							</div>
							<div className={styles.fieldItem}>
								<div className={styles.fieldLabel}>Longitude</div>
								<div className={styles.fieldValue}>{formatPrimitiveValue(dating.longitude)}</div>
							</div>
							<div className={styles.fieldItem}>
								<div className={styles.fieldLabel}>Latitude</div>
								<div className={styles.fieldValue}>{formatPrimitiveValue(dating.latitude)}</div>
							</div>
							<div className={styles.fieldItem}>
								<div className={styles.fieldLabel}>Coordinates</div>
								<div className={styles.fieldValue}>
									{dating.position?.coordinates?.join(', ') || '-'}
								</div>
							</div>

							<div className={styles.fieldItemFull}>
								<div className={styles.fieldLabel}>Looking for</div>
								<TagList items={dating.looking_for} />
							</div>

							<div className={styles.fieldItemFull}>
								<div className={styles.fieldLabel}>Languages can speak array</div>
								<TagList items={dating.languages_can_speak_array} />
							</div>

							<div className={styles.fieldItemFull}>
								<div className={styles.fieldLabel}>Filter config</div>
								<JsonValue value={dating.filter_config} />
							</div>

							<div className={styles.fieldItemFull}>
								<div className={styles.fieldLabel}>Images</div>
								{datingImages.length ? (
									<div className={styles.imageGrid}>
										{datingImages.map((image, index) => (
											<img
												alt={`dating-${index}`}
												className={styles.imageItem}
												key={`${image}-${index}`}
												src={image}
											/>
										))}
									</div>
								) : (
									<div className={styles.fieldValue}>-</div>
								)}
							</div>
						</div>
					</Accordion.Panel>
				</Accordion.Item>
			</Accordion>
		</section>
	)
}

function ActionButton({ label, icon, variant = 'primary', onClick }) {
	const variantClass =
		variant === 'danger'
			? styles.actionDanger
			: variant === 'muted'
				? styles.actionMuted
				: styles.actionPrimary

	return (
		<CustomTooltip label={label}>
			<button
				type="button"
				className={`${styles.actionButton} ${variantClass}`}
				onClick={onClick}
			>
				{icon}
				<span>{label}</span>
			</button>
		</CustomTooltip>
	)
}

function HeaderStat({ label, value }) {
	return (
		<div className={styles.headerStat}>
			<div className={styles.headerStatLabel}>{label}</div>
			<div className={styles.headerStatValue}>{value}</div>
		</div>
	)
}

function NameHistorySection({ data }: { data?: UserData }) {
	return (
		<section className={styles.section}>
			<div className={styles.sectionHeader}>
				<div>
					<h2 className={styles.sectionTitle}>Name history</h2>
					<p className={styles.sectionDescription}>
						{data?.name_history?.length ?? 0} recorded name changes.
					</p>
				</div>
			</div>
			<TableComponent
				enableRowActions={false}
				columns={[
					{
						header: 'New Name',
						accessorKey: 'new_name',
					},
					{
						header: 'Old Name',
						accessorKey: 'old_name',
					},
					{
						header: 'Created at',
						accessorKey: 'created_at',
						Cell: ({ cell }) => formatDateLikeValue(cell.getValue<string>()),
					},
				]}
				total={data?.name_history?.length ?? 0}
				data={data?.name_history ?? []}
				handleChangePage={() => {}}
				pagination={{ pageIndex: 1, pageSize: 10 }}
				loading={false}
			/>
		</section>
	)
}

function UserDetail({
	id,
	fields = userDataFields,
	label = 'Users',
	goBack = '/users',
}) {
	const navigate = useNavigate()
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<UserData>()
	const [openModalBlock, setOpenModalBlock] = useState(false)
	const [openUpdateAddress, setOpenUpdateAddress] = useState(false)
	const [formBlock, setFormBlock] = useState({ reason: '', type: 'ONE_DAY', user_id: '' })
	const selectedFieldValues = useMemo(
		() => new Set((fields as FieldType[]).map((field) => field.value)),
		[fields],
	)

	const isFieldVisible = useCallback(
		(key: string) => !defaultFieldValues.has(key) || selectedFieldValues.has(key),
		[selectedFieldValues],
	)

	const isDeleted = !!data?.deleted_at
	const isBlocked = !!data?.blocked_at

	const handleGetData = useCallback(async () => {
		openLoadingContext()
		try {
			const res = await getOneUser(id, { fields: ['$all', '$paranoid'] })
			if (res?.data?.code === 200) {
				setData(res?.data?.results?.object)
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	const handleSubmitBlock = useCallback(async () => {
		openLoadingContext()
		try {
			const res = await blockUserTemp({ ...formBlock })
			if (res?.data?.code === 200) {
				setOpenModalBlock(false)
				setFormBlock({ reason: '', type: 'ONE_DAY', user_id: '' })
				await handleGetData()
				openModal({
					type: 'success',
					label: 'User has been blocked successfully',
				})
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [closeLoadingContext, formBlock, handleGetData, openError, openLoadingContext, openModal])

	const handleUnBlockUser = useCallback(async () => {
		if (!data?.id) return

		openLoadingContext()
		try {
			const res = await unBlockUserTemp({ user_id: data.id })
			if (res?.data?.code === 200) {
				await handleGetData()
				openModal({
					type: 'success',
					label: 'User has been unblocked successfully',
				})
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [closeLoadingContext, data?.id, handleGetData, openError, openLoadingContext, openModal])

	const handleOpenUpdateStatus = useCallback(() => {
		if (!data?.id) return

		if (!isBlocked) {
			setFormBlock({ reason: '', type: 'ONE_DAY', user_id: data.id })
			setOpenModalBlock(true)
			return
		}

		openModal({
			type: 'confirm',
			label: <div>Do you want to unblock this user?</div>,
			handleConfirm: handleUnBlockUser,
		})
	}, [data?.id, handleUnBlockUser, isBlocked, openModal])

	const handleToggleVerify = useCallback(async () => {
		if (!data?.id) return

		openLoadingContext()
		try {
			const res = await setVerifyUser(data.id, !data.is_verified)
			if (res?.data?.code === 200) {
				await handleGetData()
				openModal({
					type: 'success',
					label: `User has been ${!data.is_verified ? 'verified' : 'unverified'} successfully`,
				})
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [closeLoadingContext, data?.id, data?.is_verified, handleGetData, openError, openLoadingContext, openModal])

	const handleOpenToggleVerify = useCallback(() => {
		openModal({
			type: 'confirm',
			label: data?.is_verified ? (
				<>Remove from UniVini team!</>
			) : (
				<>
					<div>Do you want to verify UniVini this user?</div>
					<div>UniVini team (can block users)</div>
				</>
			),
			handleConfirm: handleToggleVerify,
		})
	}, [data?.is_verified, handleToggleVerify, openModal])

	const handleToggleVerifyRegular = useCallback(async () => {
		if (!data?.id) return

		openLoadingContext()
		try {
			const res = await setVerifyRegularUser(data.id, !data.is_regular)
			if (res?.data?.code === 200) {
				await handleGetData()
				openModal({
					type: 'success',
					label: `User has been ${!data.is_regular ? 'Verified' : 'UnVerified'} Regular User successfully`,
				})
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [closeLoadingContext, data?.id, data?.is_regular, handleGetData, openError, openLoadingContext, openModal])

	const handleOpenToggleVerifyRegular = useCallback(() => {
		openModal({
			type: 'confirm',
			label: data?.is_regular ? (
				<>Remove from Regular User!</>
			) : (
				<>
					<div>Do you want to verify Regular User this user?</div>
					<div>Regular users (can not block users)</div>
				</>
			),
			handleConfirm: handleToggleVerifyRegular,
		})
	}, [data?.is_regular, handleToggleVerifyRegular, openModal])

	const handleUpdateAddressSuccess = useCallback(
		(updatedUser) => {
			setData((pre) => (pre ? { ...pre, ...updatedUser } : updatedUser))
			handleGetData()
		},
		[handleGetData],
	)

	useEffect(() => {
		handleGetData()
	}, [handleGetData])

	return (
		<Stack sx={{ width: '100%' }}>
			<CustomGoBack
				label={label}
				onClick={() => {
					navigate(goBack)
				}}
			/>

			<main className={styles.pageShell}>
				<section className={styles.profileHeader}>
					<div className={styles.coverWrap}>
						{data?.cover ? (
							<img alt={`${data?.name || 'User'} cover`} className={styles.coverImage} src={data.cover} />
						) : (
							<div className={styles.coverFallback} />
						)}
					</div>

					<div className={styles.profileHeaderBody}>
						<div className={styles.identityBlock}>
								<Avatar
									alt={`${data?.name || 'User'} avatar`}
									className={styles.avatar}
									radius={50}
									size={120}
									src={data?.avatar || undefined}
								>
									{String(data?.name || '?').trim().charAt(0).toUpperCase() || '?'}
								</Avatar>
							<div className={styles.identityText}>
								<div className={styles.identityTopline}>
									<span className={styles.accountType}>{formatPrimitiveValue(data?.account_type)}</span>
									{isDeleted ? <span className={`${styles.badge} ${styles.badgeDanger}`}>Deleted</span> : null}
									{isBlocked ? <span className={`${styles.badge} ${styles.badgeDanger}`}>Blocked</span> : null}
									{data?.status === false ? <span className={`${styles.badge} ${styles.badgeMuted}`}>Inactive</span> : null}
								</div>
								<h1 className={styles.profileName}>{formatPrimitiveValue(data?.name)}</h1>
								<div className={styles.profileId}>{formatPrimitiveValue(data?.id)}</div>
							</div>
						</div>

						<div className={styles.headerStats}>
							<HeaderStat label="Version" value={formatPrimitiveValue(data?.version)} />
							<HeaderStat label="Last login" value={formatTimestampValue(data?.last_login_at)} />
							<HeaderStat label="Member since" value={formatDateLikeValue(data?.created_at, 'DD/MM/YYYY')} />
							<HeaderStat label="Wallet" value={formatPrimitiveValue(data?.wallet)} />
						</div>

						{!isDeleted ? (
							<div className={styles.actionBar}>
								<ActionButton
									label="Update address"
									icon={<MyLocation fontSize="small" />}
									onClick={() => setOpenUpdateAddress(true)}
								/>
								<ActionButton
									label={isBlocked ? 'Unblock' : 'Block'}
									icon={isBlocked ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
									variant={isBlocked ? 'primary' : 'danger'}
									onClick={handleOpenUpdateStatus}
								/>
								<ActionButton
									label={data?.is_verified ? 'Unverify UniVini' : 'Verify UniVini'}
									icon={data?.is_verified ? <VerifiedUser fontSize="small" /> : <GppMaybe fontSize="small" />}
									variant={data?.is_verified ? 'primary' : 'muted'}
									onClick={handleOpenToggleVerify}
								/>
								<ActionButton
									label={data?.is_regular ? 'Unverify Regular' : 'Verify Regular'}
									icon={data?.is_regular ? <VerifiedUser fontSize="small" /> : <GppMaybe fontSize="small" />}
									variant={data?.is_regular ? 'primary' : 'muted'}
									onClick={handleOpenToggleVerifyRegular}
								/>
							</div>
						) : null}
					</div>
				</section>

				<div className={styles.contentGrid}>
					<div className={styles.mainColumn}>
						<DetailSection
							title="Account"
							description="Identity, login, and locale fields."
							fields={accountFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="Status & control"
							description="Operational state used by CMS and moderation flows."
							fields={statusFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="Profile"
							description="User-facing profile content and share assets."
							fields={profileFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="App activity"
							description="Version, login, wallet, and activity fields."
							fields={activityFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="Location"
							description="GPS and user-selected local location data."
							fields={locationFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="MB profile content"
							description="Mobile profile prompts and discovery attributes."
							fields={mbProfileFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						<DetailSection
							title="System JSON"
							description="Structured config fields kept readable for CMS checks."
							fields={systemFields}
							data={data}
							isFieldVisible={isFieldVisible}
						/>
						{data?.dating ? <DatingSection dating={data.dating} /> : null}
						<NameHistorySection data={data} />
					</div>

					<aside className={styles.sideColumn}>
						<section className={styles.section}>
							<div className={styles.sectionHeader}>
								<div>
									<h2 className={styles.sectionTitle}>Devices</h2>
									<p className={styles.sectionDescription}>Device and app version records.</p>
								</div>
							</div>
							<ListDevice userId={id} />
						</section>
					</aside>
				</div>
			</main>

			<ModalUpdateUserAddress
				open={openUpdateAddress}
				user={data}
				onClose={() => setOpenUpdateAddress(false)}
				onSuccess={handleUpdateAddressSuccess}
			/>

			<ModalForm
				open={openModalBlock}
				handleClose={() => setOpenModalBlock(false)}
				handleSubmit={handleSubmitBlock}
			>
				<Stack spacing={2} sx={{ width: '300px' }}>
					<Typography variant="h6" textAlign="center">
						Enter reason and type for blocking
						{data?.name ? (
							<>
								{': '}
								<strong>{data.name}</strong>
							</>
						) : null}
					</Typography>
					<Typography sx={{ textAlign: 'start', width: '100%' }}>Reason:</Typography>
					<TextField
						label="Reason"
						name="reason"
						value={formBlock.reason}
						onChange={(event) => setFormBlock((pre) => ({ ...pre, reason: event.target.value }))}
						fullWidth
					/>

					<Typography sx={{ textAlign: 'start', width: '100%' }}>Type:</Typography>
					<Stack className="w-100 justify-content-center" spacing={1}>
						<CustomSelect
							data={blockTypeOpts}
							value={formBlock.type}
							placeholder="Type"
							onChange={(value) => {
								setFormBlock((pre) => ({ ...pre, type: value }))
							}}
						/>
					</Stack>
				</Stack>
			</ModalForm>
		</Stack>
	)
}

export default React.memo(UserDetail)
